import React, { useState, useEffect } from 'react';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  CardElement,
  useStripe,
  useElements
} from '@stripe/react-stripe-js';
import { Button } from './ui/button';
import { 
  CreditCard,
  Loader2,
  Shield,
  AlertCircle,
  CheckCircle,
  X
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

// Stripe card element options
const cardElementOptions = {
  style: {
    base: {
      fontSize: '16px',
      color: '#424770',
      '::placeholder': {
        color: '#aab7c4',
      },
    },
    invalid: {
      color: '#9e2146',
    },
  },
  hidePostalCode: false,
};

// Payment form component (inside Stripe Elements)
const PaymentForm = ({ amount, onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [saveCard, setSaveCard] = useState(false);

  // Create payment intent when component mounts
  useEffect(() => {
    const createPaymentIntent = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/wallet/topup`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            amount_cents: Math.round(amount * 100),
            save_payment_method: saveCard
          })
        });

        const data = await response.json();
        if (response.ok) {
          setClientSecret(data.client_secret);
        } else {
          setError(data.detail || 'Failed to create payment intent');
        }
      } catch (err) {
        setError('Failed to initialize payment');
      }
    };

    if (amount > 0) {
      createPaymentIntent();
    }
  }, [amount, saveCard]);

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements || !clientSecret) {
      return;
    }

    setProcessing(true);
    setError('');

    const cardElement = elements.getElement(CardElement);

    try {
      const { error, paymentIntent } = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: cardElement,
          billing_details: {
            name: 'Wallet Top-up',
          },
        },
        setup_future_usage: saveCard ? 'off_session' : undefined,
      });

      if (error) {
        setError(error.message);
        onError?.(error.message);
      } else if (paymentIntent.status === 'succeeded') {
        onSuccess?.(paymentIntent);
      }
    } catch (err) {
      setError('Payment failed. Please try again.');
      onError?.('Payment failed');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Payment Amount */}
      <div className="bg-gray-50 rounded-lg p-4 dark:bg-gray-800">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
            Top-up Amount:
          </span>
          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
            ${amount.toFixed(2)}
          </span>
        </div>
      </div>

      {/* Card Element */}
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Card Details
        </label>
        <div className="p-3 border border-gray-300 rounded-md focus-within:ring-2 focus-within:ring-indigo-500 focus-within:border-indigo-500 dark:border-gray-600 dark:bg-gray-800">
          <CardElement options={cardElementOptions} />
        </div>
      </div>

      {/* Save card option */}
      <div className="flex items-center gap-3">
        <input
          type="checkbox"
          id="save-card"
          checked={saveCard}
          onChange={(e) => setSaveCard(e.target.checked)}
          className="w-4 h-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="save-card" className="text-sm text-gray-700 dark:text-gray-300">
          Save card for future auto-recharges
        </label>
      </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-3 dark:bg-red-900/10 dark:border-red-800">
          <div className="flex items-center">
            <AlertCircle className="w-4 h-4 text-red-600 mr-2" />
            <p className="text-sm text-red-800 dark:text-red-200">{error}</p>
          </div>
        </div>
      )}

      {/* Security Notice */}
      <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
        <Shield className="w-4 h-4" />
        <span>Your payment is secured by Stripe with 256-bit SSL encryption</span>
      </div>

      {/* Action Buttons */}
      <div className="flex gap-3 pt-4">
        <Button
          type="submit"
          disabled={!stripe || processing || !clientSecret}
          className="flex-1"
        >
          {processing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <CreditCard className="w-4 h-4 mr-2" />
          )}
          {processing ? 'Processing...' : `Pay $${amount.toFixed(2)}`}
        </Button>
        <Button
          type="button"
          onClick={onCancel}
          variant="outline"
          disabled={processing}
        >
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Main Stripe Top-up Component
export const StripeTopUpFlow = ({ isOpen, onClose, onSuccess, initialAmount = 25.00 }) => {
  const [stripePromise, setStripePromise] = useState(null);
  const [amount, setAmount] = useState(initialAmount);
  const [step, setStep] = useState('amount'); // 'amount', 'payment', 'success'
  const [paymentResult, setPaymentResult] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load Stripe publishable key
  useEffect(() => {
    const loadStripeConfig = async () => {
      try {
        // Get Stripe publishable key from backend
        const response = await fetch(`${BACKEND_URL}/api/stripe/public-key`, {
          credentials: 'include'
        });
        
        if (response.ok) {
          const data = await response.json();
          const stripe = await loadStripe(data.publishable_key);
          setStripePromise(Promise.resolve(stripe));
        } else {
          console.error('Failed to load Stripe configuration');
        }
      } catch (err) {
        console.error('Error loading Stripe:', err);
      } finally {
        setLoading(false);
      }
    };

    if (isOpen) {
      loadStripeConfig();
    }
  }, [isOpen]);

  const handleAmountSubmit = () => {
    if (amount >= 5 && amount <= 500) {
      setStep('payment');
    }
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setPaymentResult(paymentIntent);
    setStep('success');
    // Call parent success handler
    onSuccess?.(paymentIntent);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const handleClose = () => {
    setStep('amount');
    setPaymentResult(null);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md dark:bg-gray-900 relative">
        {/* Close button */}
        <button
          onClick={handleClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          <X className="w-5 h-5" />
        </button>

        <h3 className="text-lg font-semibold text-gray-900 mb-6 dark:text-gray-100">
          Add Funds to Wallet
        </h3>

        {loading ? (
          <div className="text-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
            <p className="text-gray-600 dark:text-gray-300">Loading payment system...</p>
          </div>
        ) : step === 'amount' ? (
          /* Amount Selection Step */
          <div className="space-y-4">
            <div>
              <label htmlFor="amount" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Amount (USD)
              </label>
              <input
                id="amount"
                type="number"
                min="5"
                max="500"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(parseFloat(e.target.value) || 0)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100"
                placeholder="Enter amount"
              />
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Minimum: $5.00, Maximum: $500.00
              </p>
            </div>

            {/* Quick amount buttons */}
            <div className="grid grid-cols-3 gap-2">
              {[10, 25, 50].map((quickAmount) => (
                <button
                  key={quickAmount}
                  onClick={() => setAmount(quickAmount)}
                  className="py-2 px-3 text-sm border border-gray-300 rounded-md hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-800 dark:text-gray-300"
                >
                  ${quickAmount}
                </button>
              ))}
            </div>

            <Button
              onClick={handleAmountSubmit}
              disabled={amount < 5 || amount > 500}
              className="w-full mt-6"
            >
              Continue to Payment
            </Button>
          </div>
        ) : step === 'payment' && stripePromise ? (
          /* Payment Step */
          <Elements stripe={stripePromise}>
            <PaymentForm
              amount={amount}
              onSuccess={handlePaymentSuccess}
              onError={handlePaymentError}
              onCancel={() => setStep('amount')}
            />
          </Elements>
        ) : step === 'success' ? (
          /* Success Step */
          <div className="text-center py-8">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-green-900/30">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h4 className="text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100">
              Payment Successful!
            </h4>
            <p className="text-gray-600 mb-4 dark:text-gray-300">
              ${amount.toFixed(2)} has been added to your wallet.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Transaction ID: {paymentResult?.id}
            </p>
            <Button onClick={handleClose} className="w-full">
              Done
            </Button>
          </div>
        ) : (
          /* Error State */
          <div className="text-center py-8">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h4 className="text-lg font-semibold text-gray-900 mb-2 dark:text-gray-100">
              Payment System Unavailable
            </h4>
            <p className="text-gray-600 mb-4 dark:text-gray-300">
              Unable to load payment system. Please try again later.
            </p>
            <Button onClick={handleClose} variant="outline">
              Close
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default StripeTopUpFlow;